/**
 * SearchableDropdown - Reusable dropdown with search/filter and refresh functionality
 *
 * Features:
 * - Type-ahead filtering (Tableau-like)
 * - Match count display ("5 of 100 items")
 * - Match highlighting (bold matching text)
 * - Clear button (X to clear selection)
 * - Arrow key navigation
 * - Virtual scrolling for large lists (500+ items)
 * - Optional refresh button
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import { List, ListImperativeAPI } from 'react-window';

interface SearchableDropdownProps<T> {
  options: T[];
  value: string;
  onChange: (value: string) => void;
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => string;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
  size?: 'sm' | 'lg';
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  // New Tableau-like feature props
  showMatchCount?: boolean;
  highlightMatches?: boolean;
  showClearButton?: boolean;
  enableVirtualScroll?: boolean;
  virtualScrollThreshold?: number;
}

// Constants for virtual scrolling
const ITEM_HEIGHT = 36;
const MAX_DROPDOWN_HEIGHT = 300;

export default function SearchableDropdown<T>({
  options,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  placeholder = 'Select...',
  disabled = false,
  isLoading = false,
  onRefresh,
  isRefreshing = false,
  size,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  // New props with defaults
  showMatchCount = true,
  highlightMatches = true,
  showClearButton = true,
  enableVirtualScroll,
  virtualScrollThreshold = 500,
}: SearchableDropdownProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<ListImperativeAPI>(null);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return options;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter((option) => {
      const label = getOptionLabel(option);
      return label.toLowerCase().includes(lowerSearch);
    });
  }, [options, searchTerm, getOptionLabel]);

  // Determine if we should use virtual scrolling
  const shouldVirtualize = enableVirtualScroll ?? (options.length > virtualScrollThreshold);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length]);

  // Scroll to highlighted item in virtual list
  useEffect(() => {
    if (shouldVirtualize && listRef.current && isOpen) {
      listRef.current.scrollToRow({ index: highlightedIndex, align: 'smart' });
    }
  }, [highlightedIndex, shouldVirtualize, isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(0);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = useCallback((option: T) => {
    const optionValue = getOptionValue(option);
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
  }, [getOptionValue, onChange]);

  const handleRefresh = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRefresh) {
      await onRefresh();
    }
    // Close dropdown if open
    if (isOpen) {
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(0);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    setHighlightedIndex(0);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0 && highlightedIndex < filteredOptions.length) {
        handleSelect(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlightedIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlightedIndex(Math.max(0, filteredOptions.length - 1));
    }
  };

  // Helper function to highlight matching text
  const renderHighlightedLabel = (label: string): React.ReactNode => {
    if (!highlightMatches || !searchTerm.trim()) {
      return label;
    }

    const lowerLabel = label.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    const matchIndex = lowerLabel.indexOf(lowerSearch);

    if (matchIndex === -1) {
      return label;
    }

    return (
      <>
        {label.slice(0, matchIndex)}
        <strong style={{ backgroundColor: '#fff3cd', padding: '0 2px', borderRadius: '2px' }}>
          {label.slice(matchIndex, matchIndex + searchTerm.length)}
        </strong>
        {label.slice(matchIndex + searchTerm.length)}
      </>
    );
  };

  const selectedOption = options.find((opt) => getOptionValue(opt) === value);
  const displayValue = selectedOption ? getOptionLabel(selectedOption) : '';
  const hasValue = value && value.length > 0;

  // Render a single option item
  const renderOptionItem = (option: T, index: number, style?: React.CSSProperties) => {
    const optionValue = getOptionValue(option);
    const optionLabel = getOptionLabel(option);
    const isSelected = value === optionValue;
    const isHighlighted = index === highlightedIndex;

    return (
      <div
        key={optionValue}
        role="option"
        aria-selected={isSelected}
        style={{
          ...style,
          cursor: 'pointer',
          padding: '8px 12px',
          backgroundColor: isSelected
            ? '#0d6efd'
            : isHighlighted
            ? '#e9ecef'
            : undefined,
          color: isSelected ? 'white' : undefined,
          borderBottom: '1px solid #f0f0f0',
        }}
        onMouseEnter={() => setHighlightedIndex(index)}
        onClick={() => handleSelect(option)}
      >
        {renderHighlightedLabel(optionLabel)}
      </div>
    );
  };

  // Virtual list row renderer for react-window v2
  const VirtualRow = ({ index, style }: {
    ariaAttributes: { 'aria-posinset': number; 'aria-setsize': number; role: 'listitem' };
    index: number;
    style: React.CSSProperties
  }) => {
    return renderOptionItem(filteredOptions[index], index, style);
  };

  return (
    <>
      <style>
        {`
          @keyframes searchable-dropdown-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .searchable-dropdown-clear-btn {
            border: none;
            background: transparent;
            padding: 0 8px;
            color: #6c757d;
            cursor: pointer;
            display: flex;
            align-items: center;
          }
          .searchable-dropdown-clear-btn:hover {
            color: #dc3545;
          }
        `}
      </style>
      <div ref={dropdownRef} className="position-relative">
        <InputGroup size={size}>
          <Form.Control
            ref={inputRef}
            type="text"
            value={isOpen ? searchTerm : displayValue}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setHighlightedIndex(0);
              if (!isOpen) {
                setIsOpen(true);
              }
            }}
            onFocus={() => {
              setIsOpen(true);
              setSearchTerm('');
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            id={id}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-activedescendant={
              isOpen && filteredOptions.length > 0
                ? `option-${getOptionValue(filteredOptions[highlightedIndex])}`
                : undefined
            }
          />
          {/* Clear button */}
          {showClearButton && hasValue && !disabled && !isOpen && (
            <button
              type="button"
              className="searchable-dropdown-clear-btn"
              onClick={handleClear}
              title="Clear selection"
              aria-label="Clear selection"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
          {/* Refresh button */}
          {onRefresh && (
            <Button
              variant="outline-secondary"
              onClick={handleRefresh}
              disabled={disabled || isLoading || isRefreshing}
              title="Refresh options"
              aria-label="Refresh options"
              style={{ position: 'relative' }}
            >
              <i
                className="bi bi-arrow-clockwise"
                style={
                  isRefreshing
                    ? {
                        display: 'inline-block',
                        animation: 'searchable-dropdown-spin 1s linear infinite',
                      }
                    : undefined
                }
              ></i>
            </Button>
          )}
        </InputGroup>

        {isOpen && (
          <div
            className="position-absolute w-100 bg-white border rounded shadow-lg"
            style={{
              zIndex: 1000,
              marginTop: '2px',
              top: '100%',
              overflow: 'hidden',
            }}
            role="listbox"
          >
            {/* Match count display */}
            {showMatchCount && searchTerm && (
              <div
                className="px-3 py-1 text-muted small border-bottom"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                {filteredOptions.length === options.length
                  ? `${options.length} items`
                  : `${filteredOptions.length} of ${options.length} items`}
              </div>
            )}

            {/* Options list */}
            {filteredOptions.length === 0 ? (
              <div className="p-3 text-muted text-center">
                {isLoading ? 'Loading...' : searchTerm ? 'No matches found' : 'No options available'}
              </div>
            ) : shouldVirtualize ? (
              /* Virtual scrolling for large lists */
              <List
                listRef={listRef}
                rowCount={filteredOptions.length}
                rowHeight={ITEM_HEIGHT}
                rowComponent={VirtualRow}
                rowProps={{}}
                style={{ height: Math.min(MAX_DROPDOWN_HEIGHT - (showMatchCount && searchTerm ? 28 : 0), filteredOptions.length * ITEM_HEIGHT) }}
              />
            ) : (
              /* Regular rendering for smaller lists */
              <div style={{ maxHeight: `${MAX_DROPDOWN_HEIGHT}px`, overflowY: 'auto' }}>
                {filteredOptions.map((option, index) => renderOptionItem(option, index))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
